import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';
import type { DataTableColumn } from 'mantine-datatable';
import { useCallback, useMemo, useState } from 'react';

import { useCurrencyQueryActions } from '@/api/hooks/currencyQueryActions';
import { useEstimationCostQueryActions } from '@/api/hooks/estimationCostQueryActions';
import { useEstimationItemCostQueryActions } from '@/api/hooks/estimationItemCostQueryActions';
import { useEstimationItemQueryActions } from '@/api/hooks/estimationItemQueryActions';
import type {
  CreateEstimationItemRequest,
  CurrencyResponse,
  EstimationCostResponse,
  EstimationItemResponse,
  UpsertEstimationItemCostRequest,
} from '@/api/schema/data-contracts';
import {
  ApiEstimationItemPurpose,
  ApiEstimationItemStatus,
  ApiEstimationStage,
} from '@/api/schema/data-contracts';
import { AssumptionsModal } from '@/components/Assumption';
import { Stack } from '@/components/atoms/Stack';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import { CustomDirectCostCreationModal } from '@/components/molecules/CustomDirectCostCreationModal';
import Search from '@/components/molecules/Search';
import { CommentsModal } from '@/components/organism/Comments/CommentsModal';
import { TableColumnsSelector } from '@/components/organism/DataTable/components/columnsSelector';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { TablePhasesSelector } from '@/components/organism/DataTable/components/phasesSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { messages as sidebarMessageDefinitions } from '@/components/organism/Sidebar/messages';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { useDebouncedBy } from '@/hooks/useDebounced';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import type { OmitSafely } from '@/types';
import { isTemporaryId } from '@/utils/generators';
import { getPhaseAndDescendantIds } from '@/utils/phasesFiltering';
import { sortCostTypes } from '@/utils/sorting';

import type { CostEstimationItemsGroupedTableProps } from '../../CostEstimationItemsGroupedTables';
import { Layout } from '../../Layout';
import { getCostEstimationItemDetail } from '../../mappers/estimationItemDetailMapper';
import * as estimationItemUpdateRequestMapper from '../../mappers/estimationItemUpdateRequestMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  CostEstimationItemsEditFormModel,
  CostEstimationItemsTableRecord,
  EstimationGroupActionCallback,
  EstimationItemActionCallback,
} from '../../model';
import { useCostEstimationItemsTableRecords } from '../../useEstimationItemsTableRecords';
import EstimationItemDetailModal from './EstimationItemDetailModal';
import { directCostsMessageDefinitions } from './messages';
import { TopLevelEstimationItemsForm } from './TopLevelEstimationItemsForm';

const emptyArray: Array<CostEstimationItemsTableRecord> = [];

const EstimationDetailDirectCostTab = () => {
  const { estimationId } = Route.useLoaderData();
  const directCostsMessages = useMessages(directCostsMessageDefinitions);
  const sidebarMessages = useMessages(sidebarMessageDefinitions);
  const tabMessages = useMessages(estimationTabMessageDefinitions);
  const toast = useToast();
  const [searchText, setSearchText] = useState('');
  const [hiddenCostTypeColumns, setHiddenCostTypeColumns] = useState<string[]>([]);
  const [hiddenCostPhases, setHiddenCostPhases] = useState<string[]>([]);

  const {
    isLoading: isLoadingTableRecords,
    data,
    getCostEstimationItemsTableRecordsByParent: getEstimationItemsTableRecordsByParent,
  } = useCostEstimationItemsTableRecords(estimationId);

  const { useUpdateEstimationItemCostsOfEstimationItem } = useEstimationItemCostQueryActions();
  const updateEstimationItemCostsOfEstimationItemMutation =
    useUpdateEstimationItemCostsOfEstimationItem();

  const { useUpdateEstimationItem, useCreateEstimationItem, useDeleteEstimationItem } =
    useEstimationItemQueryActions();
  const updateEstimationItemMutation = useUpdateEstimationItem();
  const createEstimationItemMutation = useCreateEstimationItem();
  const deleteEstimationItemMutation = useDeleteEstimationItem();

  const { useGetCurrencies } = useCurrencyQueryActions();
  const { data: currencies, isLoading: isLoadingCurrencies } = useGetCurrencies() as {
    data: CurrencyResponse[] | undefined;
    isLoading: boolean;
  };

  const { useGetEstimationCosts } = useEstimationCostQueryActions();
  const { data: estimationCost, isLoading: isLoadingEstimationCost } = useGetEstimationCosts(
    estimationId,
  ) as { data: EstimationCostResponse | '' | undefined; isLoading: boolean };

  const hasEditPermission = useHasEstimationPermission(estimationId, 'CAN_EDIT');

  const [keepDirtyValues, setKeepDirtyValues] = useState<boolean>(true);
  const [estimationItemIdForAssumptionsModal, setEstimationItemIdForAssumptionsModal] =
    useState<string>();
  const [estimationItemIdForCommentsModal, setEstimationItemIdForCommentsModal] =
    useState<string>();
  const [estimationItemIdsForDetailsModal, setEstimationItemIdsForDetailsModal] = useState<
    string[]
  >([]);
  const [
    estimationGroupIdForCustomDirectCostCreationModal,
    setEstimationGroupIdForCustomDirectCostCreationModal,
  ] = useState<string | null>();

  const { debounceBy: debounceEstimationItemCostsMutationBy } = useDebouncedBy(
    updateEstimationItemCostsOfEstimationItemMutation.mutate,
    2000,
  );

  const exportProps = useMemo<OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>>(
    () => ({
      estimationId,
      title: `${sidebarMessages.detailedEstimation()} - ${tabMessages.directCostLabel()}`,
      outputOptionsArray: [
        {
          showCostPurposeEstimationItems: true,
          showDirectCostColumns: true,
          showCostSumColumn: true,
        },
      ],
      hiddenCostTypeColumns,
      hiddenPhases: hiddenCostPhases,
    }),
    [estimationId, sidebarMessages, tabMessages, hiddenCostTypeColumns, hiddenCostPhases],
  );

  const estimationExporter = useEstimationExport(exportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => estimationExporter(format, undefined),
    [estimationExporter],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  const costTypeColumns: DataTableColumn<{ id: string }>[] = useMemo(() => {
    if (!data?.estimationModel) {
      return [];
    }
    const allCostTypes = sortCostTypes(
      data.estimationModel.costTypes,
      data.estimationModel.costTypeGroups,
    );
    return allCostTypes.map((costType) => ({
      accessor: costType.id,
      title: costType.name,
    }));
  }, [data]);

  const handleColumnsChange = useCallback(
    (visibleColumns: DataTableColumn<{ id: string }>[]) => {
      const visibleAccessors = new Set(visibleColumns.map((col) => col.accessor as string));
      const allAccessors = costTypeColumns.map((col) => col.accessor as string);
      const hidden = allAccessors.filter((accessor) => !visibleAccessors.has(accessor));
      setHiddenCostTypeColumns(hidden);
    },
    [costTypeColumns],
  );

  const topLevelEstimationItems = useMemo(
    () => getEstimationItemsTableRecordsByParent(undefined),
    [getEstimationItemsTableRecordsByParent],
  );

  // Filter out hidden cost phases and their descendants
  const filteredTopLevelEstimationItems = useMemo(() => {
    if (!data || hiddenCostPhases.length === 0) {
      return topLevelEstimationItems;
    }

    const hiddenPhaseAndDescendantIds = new Set<string>();
    hiddenCostPhases.forEach((phaseId) => {
      const ids = getPhaseAndDescendantIds(phaseId, data.estimationItems);
      ids.forEach((id) => hiddenPhaseAndDescendantIds.add(id));
    });

    return topLevelEstimationItems.filter((item) => !hiddenPhaseAndDescendantIds.has(item.id));
  }, [topLevelEstimationItems, hiddenCostPhases, data]);

  // Get cost phases for the selector (top-level COST items)
  const costPhasesForSelector = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.estimationItems
      .filter((item) => !item.parentId && item.purpose === ApiEstimationItemPurpose.COST)
      .map((item) => ({
        id: item.id,
        name: item.name,
      }));
  }, [data]);

  const currencyCode = useMemo(() => {
    if (!currencies || !estimationCost) {
      return undefined;
    }
    return currencies.find(
      (currency: CurrencyResponse) => currency.id === estimationCost.currencyId,
    )?.code;
  }, [currencies, estimationCost]);

  if (isLoadingTableRecords || isLoadingEstimationCost || isLoadingCurrencies) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (data === undefined || currencies === undefined) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (!data.estimationItemCosts) {
    throw new Error('Estimation item costs cannot be undefined.');
  }

  if (!estimationCost) {
    return <Layout>Estimation cost not defined yet</Layout>; // TODO: we should lock this tab when estimationCost entity hasn't been created yet
  }

  const isReadOnly =
    data.estimation.stage === ApiEstimationStage.IN_REVIEW ||
    data.estimation.stage === ApiEstimationStage.APPROVED ||
    !hasEditPermission;

  const onAssumptionsClicked: EstimationItemActionCallback = setEstimationItemIdForAssumptionsModal;
  const onCommentsClicked: EstimationItemActionCallback = setEstimationItemIdForCommentsModal;
  const onDetailsClicked: EstimationItemActionCallback = (estimationItemId: string) => {
    setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal, estimationItemId]);
  };
  // EWM-1002: the group id is optional, and passing it straight into the setter made the button
  // dead exactly when it was needed most. An estimation with no cost items renders one table with
  // no group (CostEstimationItemsGroupedTables' empty branch), so its Add button fires with
  // undefined - which is this state's "modal closed" value, so nothing opened. null is the third
  // state the modal was built for: open, with no group preselected, and the user picks one.
  const onAddEstimationItemClicked: EstimationGroupActionCallback = (estimationGroupId) =>
    setEstimationGroupIdForCustomDirectCostCreationModal(estimationGroupId ?? null);

  const tableProps: OmitSafely<
    CostEstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'onIncludeClicked' | 'onAddEstimationItemClicked' | 'searchText'
  > = {
    estimationModel: data.estimationModel,
    currencyCode: currencyCode,
    showButtons: true,
    isReadOnly,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked,
    hiddenCostTypeColumns,
  };

  const onSubmit = async (
    formModel: CostEstimationItemsEditFormModel,
    removeEstimationItemFromModelById: EstimationItemActionCallback,
  ): Promise<void> => {
    const savedEstimationItemCosts = data.estimationItemCosts;
    if (!savedEstimationItemCosts) {
      throw new Error('Estimation item costs cannot be undefined');
    }

    // delete custom items marked for deletion
    for (const estimationItemToDelete of formModel.estimationItems.filter((x) => x.shouldDelete)) {
      removeEstimationItemFromModelById(estimationItemToDelete.id);
      await deleteEstimationItemMutation.mutateAsync({
        estimationItemId: estimationItemToDelete.id,
      });
    }

    // omit/de-omit model-based items // TODO: skip if already omitted
    const omitionPromises = formModel.estimationItems
      .filter((x) => !x.shouldDelete)
      .map((formModelEstimationItem) => {
        const responseEstimationItem = data.estimationItems.find(
          (x) => x.id === formModelEstimationItem.id,
        );
        if (!responseEstimationItem) {
          throw new Error(`Estimation item id ${formModelEstimationItem.id} has not been fetched`);
        }
        const isChanged =
          formModelEstimationItem.isIncluded ===
          (responseEstimationItem.status === ApiEstimationItemStatus.OMITTED);

        if (isChanged) {
          const estimationItemResponse = data.estimationItems.find(
            (x) => x.id === formModelEstimationItem.id,
          )!;
          return updateEstimationItemMutation.mutateAsync({
            estimationItemId: formModelEstimationItem.id,
            estimationItem: {
              ...estimationItemUpdateRequestMapper.mapFromEstimationItemResponse(
                estimationItemResponse,
              ),
              status: formModelEstimationItem.isIncluded ? 'ESTIMATED' : 'OMITTED',
            },
          });
        }

        return Promise.resolve();
      });
    await Promise.all(omitionPromises);

    // identify items to update costs
    const formModelItemsToUpsertEstimationItemCosts = formModel.estimationItems.filter(
      (formModelEstimationItem) => {
        if (formModelEstimationItem.shouldDelete || !formModelEstimationItem.isIncluded) {
          return false;
        }

        const formModelItemCosts = sortBy(
          formModelEstimationItem.estimationItemCosts,
          (x) => x.costTypeId,
        );

        const allCostTypes = sortCostTypes(
          data.estimationModel.costTypes,
          data.estimationModel.costTypeGroups,
        );
        if (formModelItemCosts?.length !== allCostTypes.length) {
          throw new Error('Wrong number of costs');
        }

        const initialEstimationItemCosts = sortBy(
          savedEstimationItemCosts
            .filter((x) => x.estimationItemId === formModelEstimationItem.id)
            .map((x) => pick(x, ['costTypeId', 'price'])),
          (x) => x.costTypeId,
        );
        // if there are no direct costs for the item in DB yet and in form it's set as null for every cost type => we can ignore it
        const itemCostsUnsetYet =
          initialEstimationItemCosts.length === 0 &&
          formModelItemCosts.every((formModelItemCost) => formModelItemCost.price === null);
        const hasChanged =
          !isEqual(initialEstimationItemCosts, formModelItemCosts) && !itemCostsUnsetYet;
        return hasChanged;
      },
    );

    // update costs
    formModelItemsToUpsertEstimationItemCosts.forEach((formModelEstimationItem) => {
      const estimationItemCostsUpsertPayload = (
        formModelEstimationItem.estimationItemCosts ?? []
      ).map<UpsertEstimationItemCostRequest>((x) => pick(x, ['costTypeId', 'price']));
      debounceEstimationItemCostsMutationBy(formModelEstimationItem.id, {
        estimationItemId: formModelEstimationItem.id,
        estimationItemCosts: estimationItemCostsUpsertPayload,
      });
    });

    return Promise.resolve();
  };

  const onSubmitDetailsModalForm = async (formModel: CostEstimationItemsEditFormModel) => {
    if (!formModel.mainEstimationItem || !formModel.estimationItems) {
      throw new Error('Form model estimation items cannot be undefined');
    }

    setKeepDirtyValues(false);

    const mainEstimationItemId = formModel.mainEstimationItem.id;
    // main estimation item
    const mainEstimationItem = data.estimationItems.find((x) => x.id === mainEstimationItemId);
    if (!mainEstimationItem) {
      throw new Error('Could not find estimation item');
    }
    // TODO: only if it's changed
    await updateEstimationItemMutation.mutateAsync({
      estimationItemId: formModel.mainEstimationItem.id,
      estimationItem: {
        ...estimationItemUpdateRequestMapper.mapFromEstimationItemResponse(mainEstimationItem),
        name: formModel.mainEstimationItem.name,
        description: formModel.mainEstimationItem.description,
      },
    });

    // delete custom items marked for deletion
    const deletionPromises = formModel.estimationItems
      .filter((x) => x.shouldDelete)
      .map((estimationItemToDelete) =>
        deleteEstimationItemMutation.mutateAsync({ estimationItemId: estimationItemToDelete.id }),
      );
    await Promise.all(deletionPromises);

    // omit/de-omit model-based items // TODO: skip if already omitted
    const omitionPromises = formModel.estimationItems
      .filter((x) => !x.shouldDelete && !isTemporaryId(x.id))
      .map((formModelEstimationItem) => {
        const responseEstimationItem = data.estimationItems.find(
          (x) => x.id === formModelEstimationItem.id,
        );
        if (!responseEstimationItem) {
          throw new Error(`Estimation item id ${formModelEstimationItem.id} has not been fetched`);
        }
        const isChanged =
          formModelEstimationItem.isIncluded ===
          (responseEstimationItem.status === ApiEstimationItemStatus.OMITTED);

        if (isChanged) {
          const estimationItemResponse = data.estimationItems.find(
            (x) => x.id === formModelEstimationItem.id,
          )!;
          return updateEstimationItemMutation.mutateAsync({
            estimationItemId: formModelEstimationItem.id,
            estimationItem: {
              ...estimationItemUpdateRequestMapper.mapFromEstimationItemResponse(
                estimationItemResponse,
              ),
              status: formModelEstimationItem.isIncluded ? 'ESTIMATED' : 'OMITTED',
            },
          });
        }

        return Promise.resolve();
      });
    await Promise.all(omitionPromises);

    const buildCostUpsertPayload = (
      estimationItemId: string,
      costs: {
        costTypeId: string;
        price: number | null;
      }[],
    ): {
      estimationItemId: string;
      estimationItemCosts: UpsertEstimationItemCostRequest[];
    } => ({
      estimationItemId: estimationItemId,
      estimationItemCosts: [...costs.map((resource) => ({ ...resource }))],
    });

    const createdEstimationItemsResponses: EstimationItemResponse[] = [];

    const upsertItemCostsPayloads: ReturnType<typeof buildCostUpsertPayload>[] = [];

    // create sub-items and update its costs
    for (const formModelEstimationItem of formModel.estimationItems.filter(
      (x) => x.isIncluded && isTemporaryId(x.id),
    )) {
      const estimationItemCreatePayload: CreateEstimationItemRequest = {
        name: formModelEstimationItem.name,
        description: formModelEstimationItem.description,
        estimationModelGroupId: formModelEstimationItem.groupId,
        isParentBasedCalculation: false, // unused for direct costs
        methodologyId: undefined, // unused for direct costs
        status: 'ESTIMATED',
        unit: data.estimation.unit, // unused for direct costs
        purpose: 'COST',
        parentId: mainEstimationItemId,
        phaseLeadId: undefined,
        parameterValues: [], // unused for direct costs
      };
      const createdEstimationItem = await createEstimationItemMutation.mutateAsync({
        estimationId: data.estimation.id,
        estimationItem: estimationItemCreatePayload,
      });
      createdEstimationItemsResponses.push(createdEstimationItem);
      upsertItemCostsPayloads.push(
        buildCostUpsertPayload(
          createdEstimationItem.id,
          formModelEstimationItem.estimationItemCosts ?? [],
        ),
      );
    }

    // update sub-items' costs
    for (const formModelEstimationItem of formModel.estimationItems.filter(
      (x) => x.isIncluded && !isTemporaryId(x.id),
    )) {
      upsertItemCostsPayloads.push(
        buildCostUpsertPayload(
          formModelEstimationItem.id,
          formModelEstimationItem.estimationItemCosts!,
        ),
      );
    }

    // update main item costs based on its subcosts sum, but only if it has any sub-costs (otherwise it would be always set to 0)
    if (formModel.estimationItems.filter((x) => x.isIncluded).length) {
      // create payloads for upserting resources of parents and its parents, etc.. by calculating it as sum of its children's resources
      const addUpsertParentsCostsPayloadsRecursively = (estimationItemId: string) => {
        const estimationItem = data.estimationItems.find((x) => x.id === estimationItemId)!;

        const childCosts = getEstimationItemsTableRecordsByParent(estimationItemId)
          .filter((x) => !x.isOmitted)
          // prefer fresh values, fallback to BE values
          .map(
            (x) =>
              upsertItemCostsPayloads.find((payload) => payload.estimationItemId === x.id)
                ?.estimationItemCosts ?? x.estimationItemCosts,
          )
          .flatMap((x) => x);

        const createdSubItemsIds = createdEstimationItemsResponses
          .filter((createdItem) => createdItem.parentId === estimationItemId)
          .map((createdSubItem) => createdSubItem.id);

        // add costs of newly created sub-items (not retrieved yet from BE)
        upsertItemCostsPayloads
          .filter((x) => createdSubItemsIds.includes(x.estimationItemId))
          .flatMap((x) => x.estimationItemCosts)
          .forEach((x) => {
            childCosts.push({ costTypeId: x.costTypeId, price: x.price ?? 0 });
          });

        const parentPayload = buildCostUpsertPayload(
          estimationItem.id,
          sortCostTypes(data.estimationModel.costTypes, data.estimationModel.costTypeGroups).map(
            (costType) => ({
              costTypeId: costType.id,
              price: sumBy(
                childCosts.filter(
                  (estimationItemCost) => estimationItemCost.costTypeId === costType.id,
                ),
                (resource) => resource.price ?? 0,
              ),
            }),
          ),
        );
        upsertItemCostsPayloads.push(parentPayload);

        if (estimationItem.parentId) {
          addUpsertParentsCostsPayloadsRecursively(estimationItem.parentId);
        }
      };

      addUpsertParentsCostsPayloadsRecursively(formModel.mainEstimationItem.id);

      const upsertPromises = upsertItemCostsPayloads.map((payload) =>
        updateEstimationItemCostsOfEstimationItemMutation.mutateAsync(payload),
      );
      await Promise.all(upsertPromises);
    }

    setKeepDirtyValues(true);

    toast.showToast(
      directCostsMessages.modalSavedMessage({
        mainEstimationItemName: formModel.mainEstimationItem?.name,
      }),
      'success',
    );
  };

  return (
    <Layout>
      <Stack gap={10}>
        <div className='mt-4 flex items-center justify-between gap-2 rounded-xl'>
          <h2>{directCostsMessages.costBreakDownTitle()}</h2>
          <div className='flex gap-2'>
            <Search handleOnChange={setSearchText} />
            <ExportButtonsSelector
              exportConfiguration={exportConfiguration}
              onExportButtonClick={handleExportButtonClick}
            />
            {costPhasesForSelector.length > 0 && (
              <TablePhasesSelector
                phases={costPhasesForSelector}
                hiddenPhases={hiddenCostPhases}
                onPhasesChange={setHiddenCostPhases}
              />
            )}
            {costTypeColumns.length > 0 && (
              <TableColumnsSelector
                columns={costTypeColumns}
                onColumnsChange={handleColumnsChange}
                hiddenColumns={hiddenCostTypeColumns}
              />
            )}
          </div>
        </div>
        <TopLevelEstimationItemsForm
          topLevelEstimationItems={filteredTopLevelEstimationItems}
          tableProps={{ ...tableProps, searchText, onAddEstimationItemClicked }}
          onSubmit={onSubmit}
          keepDirtyValues={keepDirtyValues}
        />
      </Stack>
      {estimationItemIdsForDetailsModal.map((estimationItemIdForDetail) => (
        <EstimationItemDetailModal
          key={estimationItemIdForDetail}
          open={!!estimationItemIdForDetail}
          onClose={() =>
            setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal.slice(0, -1)])
          }
          onSubmit={onSubmitDetailsModalForm}
          estimationItem={
            estimationItemIdForDetail === undefined
              ? undefined
              : getCostEstimationItemDetail(estimationItemIdForDetail, data)
          }
          estimationItems={
            estimationItemIdForDetail
              ? getEstimationItemsTableRecordsByParent(estimationItemIdForDetail)
              : emptyArray
          }
          exportProps={exportProps}
          keepDirtyValues={keepDirtyValues}
          {...tableProps}
        />
      ))}
      <AssumptionsModal
        estimationId={estimationId}
        onClose={() => setEstimationItemIdForAssumptionsModal(undefined)}
        estimationItem={data.estimationItems.find(
          (x) => x.id === estimationItemIdForAssumptionsModal,
        )}
      />
      <CustomDirectCostCreationModal
        estimationModel={data.estimationModel}
        defaultGroupId={estimationGroupIdForCustomDirectCostCreationModal ?? undefined}
        open={estimationGroupIdForCustomDirectCostCreationModal !== undefined}
        onClose={() => setEstimationGroupIdForCustomDirectCostCreationModal(undefined)}
        onSave={async (formModel) => {
          if (!formModel.groupId) {
            throw new Error('Group must be chosen');
          }
          const estimationItemCreatePayload: CreateEstimationItemRequest = {
            name: formModel.name,
            description: formModel.description,
            estimationModelGroupId: formModel.groupId,
            isParentBasedCalculation: false, // unused for direct costs
            methodologyId: undefined, // unused for direct costs
            status: 'ESTIMATED',
            unit: data.estimation.unit, // unused for direct costs
            purpose: 'COST',
            parentId: undefined,
            phaseLeadId: undefined, // unused for direct costs
            parameterValues: [],
          };
          setKeepDirtyValues(false);
          await createEstimationItemMutation.mutateAsync({
            estimationId: data.estimation.id,
            estimationItem: estimationItemCreatePayload,
          });
          setKeepDirtyValues(true);
          setEstimationGroupIdForCustomDirectCostCreationModal(undefined);
        }}
      />
      <CommentsModal
        commentType='ESTIMATION_ITEM'
        commentContext='COSTING'
        size='s'
        estimationId={data.estimation.id}
        estimationItem={data.estimationItems.find((x) => x.id === estimationItemIdForCommentsModal)}
        onClose={() => setEstimationItemIdForCommentsModal(undefined)}
      />
    </Layout>
  );
};

export default EstimationDetailDirectCostTab;
